/**
 * useHeadshots
 *
 * Given a sport and a list of player names, returns { [name]: url } for the
 * ones ESPN has a headshot for. Names with no picture are simply absent, so
 * `urls[name]` is undefined and the avatar falls back to initials.
 *
 * Why not one fetch per avatar: a roster page has 15 rows, a draft review
 * 150. Every request made within the same tick is pooled into ONE POST per
 * sport, so a page of avatars costs a single round trip however many
 * components asked.
 *
 *   - Module-level cache: once a name is resolved (hit OR miss) it is never
 *     asked for again in this tab, so moving between pages that show the
 *     same players costs nothing.
 *   - Dedup: names already cached, or already in a request that is in
 *     flight, are left out of the next POST.
 *   - Kill switch: VITE_PLAYER_HEADSHOTS=false makes the hook return {}
 *     without ever calling the API (the backend has its own switch too).
 *   - Never throws: a failed request marks those names as misses so the
 *     page settles on initials instead of retrying forever.
 */
import { useEffect, useMemo, useState } from 'react'
import { playersApi } from '../services/api'

export const HEADSHOTS_ENABLED = import.meta.env.VITE_PLAYER_HEADSHOTS !== 'false'

/** Server caps a request at 300 names; stay under it per POST. */
const BATCH = 300

/** sport -> Map<name, url|null>. null = asked, no picture. */
const cache = new Map()
/** sport -> Set<name> currently in flight (queued or requested). */
const pending = new Map()
/** sport -> Set<name> waiting for the next flush. */
const queue = new Map()
/** sport -> timer id for the pending flush. */
const timers = new Map()
/** Components to re-render when any batch lands. */
const listeners = new Set()

const bucket = (store, sport, make) => {
  if (!store.has(sport)) store.set(sport, make())
  return store.get(sport)
}
const notify = () => { listeners.forEach((fn) => fn()) }

/** Test/debug hook — forget everything resolved so far. */
export const _resetHeadshotCache = () => {
  cache.clear(); pending.clear(); queue.clear()
  timers.forEach((t) => clearTimeout(t)); timers.clear()
}

const sendBatch = async (sport, names) => {
  const known = bucket(cache, sport, () => new Map())
  const inflight = bucket(pending, sport, () => new Set())
  try {
    const { data } = await playersApi.headshots(sport, names)
    const got = data?.headshots || {}
    names.forEach((n) => known.set(n, got[n] ?? null))
  } catch {
    // A miss is a miss; the UI shows initials. Do not retry in a loop.
    names.forEach((n) => known.set(n, null))
  } finally {
    names.forEach((n) => inflight.delete(n))
    notify()
  }
}

const flush = (sport) => {
  timers.delete(sport)
  const q = queue.get(sport)
  if (!q || q.size === 0) return
  const names = [...q]
  q.clear()
  for (let i = 0; i < names.length; i += BATCH) sendBatch(sport, names.slice(i, i + BATCH))
}

/** Queue names for the next tick's POST; skips anything known or in flight. */
const request = (sport, names) => {
  const known = bucket(cache, sport, () => new Map())
  const inflight = bucket(pending, sport, () => new Set())
  const q = bucket(queue, sport, () => new Set())
  let added = false
  for (const n of names) {
    if (known.has(n) || inflight.has(n)) continue
    inflight.add(n); q.add(n); added = true
  }
  if (added && !timers.has(sport)) timers.set(sport, setTimeout(() => flush(sport), 0))
}

const sportKey = (sport) => {
  const k = String(sport || '').toLowerCase()
  return k === 'nba' || k === 'nfl' ? k : null
}

/**
 * @param {string} sport  'nba' | 'nfl' (anything else -> {})
 * @param {string[]} names  player display names as shown on the page
 * @returns {Record<string, string>} name -> url, only for names with a picture
 */
export default function useHeadshots(sport, names) {
  const key = sportKey(sport)
  const enabled = HEADSHOTS_ENABLED && Boolean(key)

  // Deduped, string-only list, folded into ONE string so the effects below
  // do not re-run when a caller passes a fresh array with the same contents
  // on every render (which most pages do).
  const wantedKey = useMemo(() => {
    if (!enabled || !Array.isArray(names)) return ''
    return [...new Set(names.filter((n) => typeof n === 'string' && n.trim()))].join('\u0001')
  }, [enabled, names])
  const wanted = useMemo(() => (wantedKey ? wantedKey.split('\u0001') : []), [wantedKey])

  // Bumped whenever a batch lands so the memo below re-reads the cache.
  const [tick, setTick] = useState(0)

  useEffect(() => {
    if (!enabled) return undefined
    const fn = () => setTick((t) => t + 1)
    listeners.add(fn)
    return () => { listeners.delete(fn) }
  }, [enabled])

  useEffect(() => {
    if (enabled && wanted.length > 0) request(key, wanted)
  }, [enabled, key, wanted])

  // Recomputed on every render (a few hundred Map reads at most). `tick`
  // changing is what causes the re-render once a batch lands; reading it here
  // keeps that intent visible.
  void tick
  const out = {}
  const known = enabled ? cache.get(key) : null
  if (!known) return out
  for (const n of wanted) {
    const url = known.get(n)
    if (url) out[n] = url
  }
  return out
}
